import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, TableContainer, TablePagination } from '@mui/material';
import CSVExportButton from '../common/buttons/CSVExportButton';
import PrintTableButton from '../common/buttons/PrintButton';
import TimeFilterDropdown from '../common/buttons/TimeFilterDropdown';
import PhenIgnoreFilterButton from '../common/buttons/PhenIgnoreFilterButton';
import BaselinesCard from '../common/cards/BaselinesCard';
import ScoreCard from '../common/cards/ScoreCard';
import SystemCard from '../common/cards/SystemCard';
import UniversalDataTable, {ALERTS_COLUMNS} from '../common/tables/UniversalTable';
import SearchBar from '../common/search/SearchBar';
import PageHeader from '../common/banner/pageHeader';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import { toast } from 'react-toastify';
import License from './license';
import { sortAlertItems } from '../common/utils/tableUtil';

/**
 * Filter alerts based on time range between first_seen and last_seen
 */
const getTimeFilteredAlerts = (alerts, filterValue) => {
  if (filterValue === 'show-all') return alerts;
  
  const filterOptions = {
    '15min': 15,
    '35min': 35, 
    '1hour': 60,
    '4hour': 240,
    '1day': 1440,
    '1week': 10080
  };
  
  const maxMinutes = filterOptions[filterValue];
  if (!maxMinutes) return alerts;
  
  const endDate = new Date();
  const beginDate = new Date(endDate);

  beginDate.setMinutes(endDate.getMinutes() - maxMinutes);

  return alerts.filter(alert => {
    const firstSeen = new Date(alert.first_seen);
    
    if (isNaN(firstSeen)) return false;
    
    if ( firstSeen >= beginDate && firstSeen <= endDate ) {
      return alert;
    }
  });
};

const getPhenIgnoreFilteredAlerts = (alerts, filterValue) => {
  if (filterValue === 'all') return alerts;
  
  return alerts.filter(alert => {
    if (filterValue === 'ignored') {
      return alert.phen_ignore === true;
    } else if (filterValue === 'active') {
      return alert.phen_ignore === false || alert.phen_ignore === null || alert.phen_ignore === undefined;
    }
    return true;
  });
};

/**
 * Main Security Dashboard Component
 * Displays overview cards and detailed alerts table with search and pagination
 * Manages all state for alerts, sorting and filtering
 */
export default function SecurityDashboard() {
  /**
   * Backend Call to the Database
   */
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ high: 0, medium: 0, low: 0 });
  const [sysCounts, setSysCounts] = useState({ authorized: 0, rogue: 0 });
  const [appCounts, setAppCounts] = useState({ authorized: 0, rogue: 0 });
  
  const handleGet = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/alerts/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error(`Failed to fetch alerts: ${res.status}`);
      
      const data = await res.json();
      setAlerts(data.data);
      
      // Count severities
      const severityCounts = data.data.reduce(
        (acc, item) => {
          const sev = item.severity.toLowerCase();
          if (acc[sev] !== undefined) acc[sev]++;
          return acc;
        },
        { high: 0, medium: 0, low: 0 }
      );
      setCounts(severityCounts);

      const resSystems = await fetch(`${process.env.REACT_APP_API}/systems/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error(`Failed to fetch system counts: ${res.status}`);
      
      const sysData = await resSystems.json();

      const appSystems = await fetch(`${process.env.REACT_APP_API}/applications/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error(`Failed to fetch application counts: ${res.status}`);
      
      const sysCounts = sysData.data.reduce(
        (sys, item) => {
          const authorized = item.authorized;
          if ( authorized ) {
            sys.authorized++;
          }  else {
            sys.rogue++;
          }
          return sys;
        },
        { authorized: 0, rogue: 0 }
      );
      await setSysCounts(sysCounts);

      const appData = await appSystems.json();

      const appCounts = appData.data.reduce(
        (acc, item) => {
          const authorized = item.authorized;
          if ( authorized ) {
            acc.authorized++;
          }  else {
            acc.rogue++;
          }
          return acc;
        },
        { authorized: 0, rogue: 0 }
      );
      await setAppCounts(appCounts);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching alerts:", err);
    }
  };
  
  useEffect(() => {
    handleGet();
  }, []);

  // UI State
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [timeFilter, setTimeFilter] = useState('show-all');
  const [phenIgnoreFilter, setPhenIgnoreFilter] = useState('active');
  const [loading, setLoading] = useState(true);

  // Event Handlers
  
  /**
   * Handle toggle actions (like ignore switch)
   * Updates specific alert properties
   */
  const handleToggle = async (alert, key, value) => {
    // Handle phen_ignore toggle with backend sync
    if (key === 'phen_ignore') {
      try {
        // Optimistically update the UI first
        setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, [key]: value } : a));
        
        // Make API call to update backend
        const res = await fetch(`${process.env.REACT_APP_API}/alerts/${alert.id}/ignore`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({ 
            phen_ignore: value 
          })
        });

        await res.json();
        if (!res.ok) {
          toast.error(`Failed to update ignore status: ${res.status}`);
        } else {
          toast.success(`${alert.sensor} Sensor Updated`);
        }
      } catch (err) {
        toast.error("Error updating ignore status : ", err);
        
        // Revert the optimistic update on error
        setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, [key]: !value } : a));
        
        // Optional: Show error message to user
        // You might want to add a toast notification here
        alert('Failed to update ignore status. Please try again.');
      }
    } 
  };

  /**
   * Handle sort configuration changes
   * Updates the sort criteria for the table
   */
  const handleSortChange = (newConfig) => {
    setSortConfig(newConfig);
    setPage(0);
  };

  // Data Processing Pipeline
  
  // apply time filter
  const timeFiltered = getTimeFilteredAlerts(alerts, timeFilter);
  
  // apply phen ignore filter
  const phenIgnoreFiltered = getPhenIgnoreFilteredAlerts(timeFiltered, phenIgnoreFilter);

  // Then apply search filter to time-filtered results
  const filtered = phenIgnoreFiltered.filter((alert) => {
    const s = searchQuery.toLowerCase();
    return ['sensor', 'source', 'message'].some((field) => 
      alert[field]?.toLowerCase().includes(s)
    );
  });

  // Apply sorting to filtered results
  const sorted = sortAlertItems(filtered, sortConfig);
  
  // Apply pagination to sorted results
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!loading) {
    return (
      <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>      
          <ClassificationBannerHeader />
          <PageHeader title="Alerts" />
          {/* Dashboard Overview Cards Section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            mb: 4
          }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                mb: 4
              }}>
                {/* Security Score Summary Card */}
                <ScoreCard
                  title="Site Score Summary"
                  high={counts.high}
                  medium={counts.medium}
                  low={counts.low}
                />
                
                {/* Systems Status Card */}
                <SystemCard
                  title="Systems"
                  authorized={sysCounts.authorized}
                  rogue={sysCounts.rogue}
                  buttonText="Provide Systems Acceptance"
                  direct= "/systems"
                  const toolTip = 'This is a count of all systems Phen has discovered in the network. Authorized are all approved systems in the network while Rogue are only new systems discovered after the 1 time 24 hour baseline (see simulation).  Based on compliance... "A human has to authorize a new systems." (IE: CheckMate, Phen.AI can not determine a system is authorized)'
                />
                
                {/* Applications Status Card */}
                <SystemCard
                  title="Applications"
                  authorized={appCounts.authorized}
                  rogue={appCounts.rogue}
                  buttonText="Provide Application Acceptance"
                  direct= "/applications"
                  const toolTip = 'This is a count of all applications Phen has discovered in the network. Like Systems Authorized and Rogue work the same.'
                />
              </Box>
              <Box sx={{ 
                  pl: 20,
                  display: 'flex',
                  justifyContent: 'flex-end'
              }}>
                {/* Baselines Status Card */}
                <BaselinesCard
                  title="Baselines"
                />
              </Box>
          </Box>

          {/* Security Alerts Table Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search alerts..."
                label="Filter"
                width={200}
              />
              
              <CSVExportButton
                endpoint="/alerts"
                params={{ format: 'csv' }}
                filename="alerts.csv"
                buttonText="Export"
              />
              <PrintTableButton
                tableData={alerts}
                title="Security Alerts Report"
                buttonText="Print"
              />
              <TimeFilterDropdown
                value={timeFilter}
                onChange={(value) => {
                  setTimeFilter(value);
                  setPage(0); // Reset to first page when filter changes
                }}
              />
              <PhenIgnoreFilterButton
                value={phenIgnoreFilter}
                onChange={(value) => {
                  setPhenIgnoreFilter(value);
                  setPage(0); // Reset to first page when filter changes
                }}
              />
            </Box>

            {/* Main Alerts Table with Pagination */}
            <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', mb: 2 }}>

              <UniversalDataTable
                data={paginated}
                columns={ALERTS_COLUMNS}
                onToggle={handleToggle}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
                itemKey="id" // Alerts use 'id' instead of 'no'
              />

              {/* Pagination Controls */}
              <TablePagination
                component="div"
                count={sorted.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
              />
            </TableContainer>
          </Box>
          <License />
        </Box>
      </Container>
    )
  }
}